/**
 * JPG Academy BarChart
 *
 * A reusable bar chart component wrapping recharts with JPG Academy brand colors.
 * Designed to be generic enough for use across all JPG apps (announces, required-services, etc.).
 *
 * Usage:
 *   <BarChart
 *     data={[{ month: 'Jun', regular: 12.5, emergency: 3.0 }, ...]}
 *     bars={[
 *       { dataKey: 'regular', name: 'Regular', color: '#1B2A4A' },
 *       { dataKey: 'emergency', name: 'Emergency', color: '#8B1A2B' },
 *     ]}
 *     xAxisKey="month"
 *   />
 */

import * as React from 'react'
import {
	ResponsiveContainer,
	BarChart as RechartsBarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
} from 'recharts'
import { cn } from '../../utils/cn'

// ── Types ───────────────────────────────────────────────────────────────────

/** A single data point in the chart */
export interface BarChartData {
	[key: string]: string | number
}

/** Configuration for a single bar series */
export interface BarSeries {
	/** Data key matching a property in the data array */
	dataKey: string
	/** Display name in legend and tooltip */
	name?: string
	/** Bar fill color (hex). Defaults to brand palette rotation. */
	color?: string
	/** Stack ID for stacked bars. Omit for grouped bars. */
	stackId?: string
}

export interface BarChartProps {
	/** Array of data objects, each representing a category on the X axis */
	data: BarChartData[]
	/** Bar series configuration */
	bars: BarSeries[]
	/** Key for X axis labels (must exist in data objects) */
	xAxisKey: string
	/** Chart height in pixels. Default 300. */
	height?: number
	/** Show horizontal grid lines. Default true. */
	showGrid?: boolean
	/** Show legend below the chart. Default true. */
	showLegend?: boolean
	/** Show tooltip on hover. Default true. */
	showTooltip?: boolean
	/** Format numeric values in tooltips. Default: number formatted with 2 decimals. */
	formatValue?: (value: number) => string
	/** Additional class names on the outer container */
	className?: string
}

// ── Brand color palette for default bar colors ──────────────────────────────

const BRAND_COLORS = [
	'#1B2A4A', // navy
	'#8B1A2B', // maroon
	'#C4972A', // gold
	'#2F4F73', // navy-500
	'#C13344', // maroon-500
	'#4A6D97', // navy-400
] as const

// ── Component ───────────────────────────────────────────────────────────────

export function BarChart({
	data,
	bars,
	xAxisKey,
	height = 300,
	showGrid = true,
	showLegend = true,
	showTooltip = true,
	formatValue,
	className,
}: BarChartProps) {
	const fmt = formatValue ?? ((v: number) => v.toFixed(2))

	return (
		<div className={cn('w-full', className)}>
			<ResponsiveContainer width="100%" height={height}>
				<RechartsBarChart
					data={data}
					margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
				>
					{showGrid && (
						<CartesianGrid
							strokeDasharray="3 3"
							vertical={false}
							stroke="hsl(219 25% 87%)"
						/>
					)}
					<XAxis
						dataKey={xAxisKey}
						tick={{ fontSize: 12, fill: 'hsl(219 25% 40%)' }}
						axisLine={{ stroke: 'hsl(219 25% 80%)' }}
						tickLine={false}
					/>
					<YAxis
						tick={{ fontSize: 12, fill: 'hsl(219 25% 40%)' }}
						axisLine={false}
						tickLine={false}
						width={50}
						tickFormatter={(v: number) => fmt(v)}
					/>
					{showTooltip && (
						<Tooltip
							formatter={(value) => [fmt(Number(value))] as const}
							cursor={{ fill: 'hsl(219 25% 95% / 0.5)' }}
							contentStyle={{
								borderRadius: '0.5rem',
								border: '1px solid hsl(219 25% 87%)',
								fontSize: '0.875rem',
								boxShadow: '0 4px 6px -1px rgb(27 42 74 / 0.08)',
							}}
							labelStyle={{
								fontSize: '0.75rem',
								fontWeight: 600,
								textTransform: 'uppercase' as const,
								letterSpacing: '0.05em',
								color: 'hsl(219 25% 40%)',
								marginBottom: 4,
							}}
						/>
					)}
					{showLegend && (
						<Legend
							wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
							iconType="rect"
							iconSize={10}
						/>
					)}
					{bars.map((bar, i) => (
						<Bar
							key={bar.dataKey}
							dataKey={bar.dataKey}
							name={bar.name ?? bar.dataKey}
							fill={bar.color ?? BRAND_COLORS[i % BRAND_COLORS.length]}
							stackId={bar.stackId}
							radius={[2, 2, 0, 0]}
							maxBarSize={48}
						/>
					))}
				</RechartsBarChart>
			</ResponsiveContainer>
		</div>
	)
}
